import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  useSidebar,
} from "@/components/ui/sidebar";
import { ChevronDown, LogOut, Settings, Gamepad2 } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { AppSwitcher } from "./app-switcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  perkpointScopedGroups,
  sidebarConfigs,
  SidebarConfig,
  SidebarItem,
} from "@/config/sidebar-config";
import { usePerkPointScope } from "@/pages/perkpoint/programs/program-scope";
import { openPerkPointSettings } from "@/pages/perkpoint/settings/PerkPointSettingsModal";
import { useUserInfo } from "@/hooks/use-user-info";
import { AuthApi } from "@/api/auth.api";
import { clearAuthStorage } from "@/lib/auth-storage";
import { PermissionGuard } from "@/components/common/PermissionGuard";
import { PERMISSION_CODES } from "@/constants/permissions";
import {
  shouldHideAccessPointAccountingEntries,
  shouldHideAnalytics,
  shouldHideExtrasSection,
} from "@/utils/role.utils";

interface UnifiedSidebarProps {
  app:
    | "accesspoint"
    | "perkpoint"
    | "gamepoint"
    | "xpandpoint"
    | "rewardpoint"
    | "giftcard"
    | "offers";
}

function AppIconFileNameFromConfig(config: SidebarConfig) {
  switch (config.appShortName) {
    case "AP":
      return "AccessPoint.svg";
    case "XP":
      return "XpandPoint.svg";
    case "RP":
      return "RewardPoint.svg";
    case "PP":
      return "PerkPoint.svg";
    case "GC":
      return "GiftCard.svg";
    case "OP":
    case "OF":
      return "Offers.svg";
    default:
      return "AccessPoint.svg";
  }
}

function SidebarHeaderAppIcon({ config }: { config: SidebarConfig }) {
  if (config.appShortName === "GP") {
    return (
      <Gamepad2 className="h-[22px] w-[22px] text-white" strokeWidth={2} />
    );
  }
  return (
    <img
      src={`/app-icons/${AppIconFileNameFromConfig(config)}`}
      alt={config.appName}
      style={{ width: 30, height: 30 }}
    />
  );
}

export function UnifiedSidebar({ app }: UnifiedSidebarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { expanded, setMobileOpen } = useSidebar();
  const { toast } = useToast();
  const { userInfo, getUserInitials, formatName, formatRole } = useUserInfo();

  const perkScope = usePerkPointScope();
  const baseConfig: SidebarConfig = sidebarConfigs[app];
  const config: SidebarConfig =
    app === "perkpoint" && perkScope.mode !== "full"
      ? {
          ...baseConfig,
          groups: perkpointScopedGroups(
            perkScope.mode,
            perkScope.session?.programId,
          ),
        }
      : baseConfig;

  // Function to handle navigation clicks on mobile
  const handleMobileNavigation = () => {
    // Close mobile sidebar when navigation item is clicked
    setMobileOpen(false);
  };

  const filteredGroups = config.groups
    .map((group) => {
      let items = group.items;

      if (
        app === "accesspoint" &&
        shouldHideAccessPointAccountingEntries(userInfo?.roleCode)
      ) {
        items = items.filter((item) => item.name !== "Accounting Entries");
      }
      if (app === "xpandpoint" && userInfo?.roleCode === "PROGRAM_ADMIN") {
        items = items.filter(
          (item) =>
            item.name !== "Accounting Entries" &&
            (item.name === "Analytics" || item.name !== "Analytics"),
        );
      }
      if (app === "rewardpoint" && userInfo?.roleCode === "PROGRAM_ADMIN") {
        items = items.filter(
          (item) =>
            item.name === "Programs" ||
            item.name === "Redemption Merchants" ||
            item.name === "Invoices" ||
            item.name === "Storefront" ||
            item.name === "Orders",
        );
      }
      if (shouldHideAnalytics(userInfo?.roleCode)) {
        items = items.filter((item) => item.name !== "Analytics");
      }

      return { ...group, items };
    })
    .filter((group) => {
      // Hide entire Extras section for PROGRAM_ADMIN and MERCHANT_ADMIN
      if (
        (app === "accesspoint" ||
          app === "xpandpoint" ||
          app === "rewardpoint") &&
        shouldHideExtrasSection(userInfo?.roleCode)
      ) {
        return group.label !== "Extras";
      }
      return true;
    });

  const isActive = (path?: string) => {
    if (!path) return false;

    // Handle root path for Access Point
    if (path === "/") {
      return location.pathname === "/";
    }

    // Handle root path for Access Point
    if (path === "/accesspoint") {
      return location.pathname === "/accesspoint";
    }

    // Handle PerkPoint dashboard
    if (path === "/perkpoint") {
      return location.pathname === "/perkpoint";
    }

    // Handle XpandPoint dashboard
    if (path === "/xpandpoint") {
      return location.pathname === "/xpandpoint";
    }

    if (path === "/rewardpoint") {
      return location.pathname === "/rewardpoint";
    }

    // Offer Point dashboard
    if (path === "/offers") {
      return (
        location.pathname === "/offers" || location.pathname === "/offers/"
      );
    }

    // Handle Gift Card paths specifically
    if (path === "/giftcard") {
      return location.pathname === "/giftcard";
    }

    if (path === "/giftcard/allissuedgiftcards") {
      return (
        location.pathname === "/giftcard/allissuedgiftcards" ||
        location.pathname.startsWith("/giftcard/allissuedgiftcards")
      );
    }

    // Treat /giftcard/create (Issue Gift Card) as its own active path
    if (path === "/giftcard/issue") {
      return location.pathname === "/giftcard/issue";
    }

    if (path === "/giftcard/redeem") {
      return (
        location.pathname === "/giftcard/redeem" ||
        location.pathname.startsWith("/giftcard/redeem")
      );
    }

    if (path === "/giftcard/logs") {
      return (
        location.pathname === "/giftcard/logs" ||
        location.pathname.startsWith("/giftcard/logs")
      );
    }

    if (path === "/giftcard/all-giftcards") {
      return location.pathname.startsWith("/giftcard/all-giftcards");
    }

    // Exact match for nested program/member pages so they do not
    // collide with /program/:id or /member/:id routes
    if (
      path === "/perkpoint/program/setup" ||
      path === "/perkpoint/member/all" ||
      path === "/perkpoint/member/history" ||
      path === "/perkpoint/member/segments" ||
      path === "/perkpoint/member/insight" ||
      path === "/perkpoint/rewards/rules" ||
      path === "/perkpoint/rewards/tiers" ||
      path === "/perkpoint/rewards/campaigns" ||
      path === "/perkpoint/rewards/coupons" ||
      path === "/perkpoint/rewards/gamification" ||
      path === "/perkpoint/audit/history"
    ) {
      if (
        path === "/perkpoint/rewards/campaigns" ||
        path === "/perkpoint/program/setup"
      ) {
        return (
          location.pathname === path ||
          location.pathname.startsWith(`${path}/`)
        );
      }
      return location.pathname === path;
    }

    // For all other paths, check if current path starts with the menu path
    return location.pathname.startsWith(path);
  };

  const isChildActive = (item: SidebarItem) =>
    Boolean(item.children?.some((child) => isActive(child.href)));

  const [openMenus, setOpenMenus] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const nextOpen: Record<string, boolean> = {};
    filteredGroups.forEach((group) => {
      group.items.forEach((item) => {
        if (item.children?.length && isChildActive(item)) {
          nextOpen[item.name] = true;
        }
      });
    });
    if (Object.keys(nextOpen).length) {
      setOpenMenus((prev) => ({ ...prev, ...nextOpen }));
    }
  }, [location.pathname]);

  const toggleMenu = (name: string) => {
    setOpenMenus((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const handleLogout = async () => {
    try {
      await AuthApi.doLogout();
    } catch (err) {
      toast({
        title: "Logout failed",
        description: err?.message || "Could not log out from server.",
        variant: "destructive",
      });
    }
    clearAuthStorage();
    navigate("/login");
    toast({
      title: "Logged out successfully",
      description: "You have been logged out of your account.",
    });
  };

  return (
    <Sidebar className="flex flex-col h-full border-r border-gray-200 bg-white shadow-sm">
      <SidebarHeader className="flex h-16 items-center border-b border-gray-200 px-4">
        <div className="flex items-center justify-between w-full">
          {expanded ? (
            <div className="flex items-center gap-2">
              <div
                className="h-8 w-8 rounded-md flex items-center justify-center"
                style={{ backgroundColor: config.appColor }}
              >
                <SidebarHeaderAppIcon config={config} />
              </div>
              <span className="font-semibold text-gray-800">
                {config.appName}
              </span>
            </div>
          ) : (
            <div
              className="h-8 w-8 mx-auto rounded-md flex items-center justify-center"
              style={{ backgroundColor: config.appColor }}
            >
              <SidebarHeaderAppIcon config={config} />
            </div>
          )}
        </div>
      </SidebarHeader>
      <div
        className="flex-1"
        style={{ overflowY: "auto", maxHeight: "calc(100vh - 64px - 64px)" }}
      >
        {/* Render filtered groups instead of config.groups */}
        {filteredGroups.map((group, groupIndex) => (
          <SidebarGroup
            key={groupIndex}
            className={
              groupIndex > 0 ? (group.label === "Extras" ? "mt-4" : "mt-6") : ""
            }
          >
            {group.label && expanded && (
              <SidebarGroupLabel className="px-3 text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">
                {group.label}
              </SidebarGroupLabel>
            )}
            <SidebarGroupContent>
              <SidebarMenu>
                {group.items.map((item) => {
                  const active = isActive(item.href);
                  const childActive = isChildActive(item);
                  const isOpen = openMenus[item.name] ?? childActive;
                  // Compute a faded version of the app color for background (10% opacity)
                  const fadedAppColor = config.appColor.startsWith("#")
                    ? `${config.appColor}1A` // 1A is ~10% opacity in hex
                    : "rgba(0,0,0,0.05)";

                  // Check if this is a Programs item that needs permission guard
                  const isProgramsItem = item.name === "Programs";
                  const needsProgramPermission =
                    isProgramsItem &&
                    (app === "accesspoint" ||
                      app === "rewardpoint" ||
                      app === "xpandpoint");

                  // Check if this is a Merchants item that needs permission guard for accesspoint
                  const isMerchantsItem = item.name === "Merchants";
                  const needsMerchantPermission =
                    isMerchantsItem && app === "accesspoint";

                  // Check if this is a Transactions item that needs permission guard for accesspoint and xpandpoint
                  const isTransactionsItem = item.name === "Transactions";
                  const needsTransactionPermission =
                    isTransactionsItem &&
                    (app === "accesspoint" || app === "xpandpoint");

                  // Check if this is an Invoices item that needs permission guard for accesspoint, rewardpoint, and xpandpoint
                  const isInvoicesItem = item.name === "Invoices";
                  const needsInvoicePermission =
                    isInvoicesItem &&
                    (app === "accesspoint" ||
                      app === "rewardpoint" ||
                      app === "xpandpoint");

                  if (item.children?.length) {
                    const nestedMenu = (
                      <SidebarMenuItem key={item.name}>
                        <button
                          type="button"
                          onClick={() => {
                            if (!expanded) {
                              const firstChild = item.children?.[0];
                              if (firstChild?.href) {
                                navigate(firstChild.href);
                                handleMobileNavigation();
                              }
                              return;
                            }
                            toggleMenu(item.name);
                          }}
                          className={cn(
                            "flex w-full items-center rounded-md p-2 text-muted-foreground",
                            (childActive || isOpen) && "font-semibold",
                          )}
                          style={
                            childActive
                              ? { backgroundColor: fadedAppColor }
                              : undefined
                          }
                        >
                          <item.icon
                            className={cn("h-5 w-5 min-w-5 stroke-[1.5px]")}
                            style={
                              childActive ? { color: config.appColor } : {}
                            }
                          />
                          {expanded && (
                            <>
                              <span
                                className="ml-3 flex-1 text-left text-sm"
                                style={
                                  childActive
                                    ? { color: config.appColor }
                                    : undefined
                                }
                              >
                                {item.name}
                              </span>
                              <ChevronDown
                                className={cn(
                                  "h-4 w-4 shrink-0 transition-transform",
                                  isOpen && "rotate-180",
                                )}
                                style={
                                  childActive
                                    ? { color: config.appColor }
                                    : undefined
                                }
                              />
                            </>
                          )}
                        </button>
                        {expanded && isOpen && (
                          <ul className="mt-1 ml-4 space-y-1 border-l border-gray-200 pl-2">
                            {item.children.map((child) => {
                              const childIsActive = isActive(child.href);
                              return (
                                <li key={child.name}>
                                  <Link
                                    to={child.href || "#"}
                                    onClick={handleMobileNavigation}
                                    className={cn(
                                      "flex items-center rounded-md p-2 text-muted-foreground",
                                      childIsActive && "font-semibold",
                                    )}
                                    style={
                                      childIsActive
                                        ? { backgroundColor: fadedAppColor }
                                        : undefined
                                    }
                                  >
                                    <child.icon
                                      className={cn(
                                        "h-4 w-4 min-w-4 stroke-[1.5px]",
                                      )}
                                      style={
                                        childIsActive
                                          ? { color: config.appColor }
                                          : {}
                                      }
                                    />
                                    <span
                                      className="ml-3 text-sm"
                                      style={
                                        childIsActive
                                          ? { color: config.appColor }
                                          : undefined
                                      }
                                    >
                                      {child.name}
                                    </span>
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </SidebarMenuItem>
                    );
                    return nestedMenu;
                  }

                  if (item.opensModal) {
                    return (
                      <SidebarMenuItem key={item.name}>
                        <SidebarMenuButton asChild>
                          <button
                            type="button"
                            onClick={() => {
                              handleMobileNavigation();
                              openPerkPointSettings();
                            }}
                            className="flex w-full items-center p-2 rounded-md text-muted-foreground"
                          >
                            <item.icon className="h-5 w-5 min-w-5 stroke-[1.5px]" />
                            {expanded && <span className="ml-3 text-sm">{item.name}</span>}
                          </button>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    );
                  }

                  const menuItem = (
                    <SidebarMenuItem key={item.name}>
                      <SidebarMenuButton
                        asChild
                        active={!item.external && active}
                      >
                        {item.external ? (
                          <a
                            href={item.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={handleMobileNavigation}
                            className="flex items-center p-2 rounded-md text-muted-foreground"
                          >
                            <item.icon
                              className={cn("h-5 w-5 min-w-5 stroke-[1.5px]")}
                            />
                            {expanded && (
                              <span className="ml-3 text-sm">{item.name}</span>
                            )}
                          </a>
                        ) : (
                          <Link
                            to={item.href || "#"}
                            onClick={handleMobileNavigation}
                            className={cn(
                              "flex items-center p-2 rounded-md text-muted-foreground ",
                              active && "font-semibold",
                            )}
                            style={
                              active ? { backgroundColor: fadedAppColor } : {}
                            }
                          >
                            <item.icon
                              className={cn("h-5 w-5 min-w-5 stroke-[1.5px]")}
                              style={active ? { color: config.appColor } : {}}
                            />
                            {expanded && (
                              <span
                                className="ml-3 text-sm"
                                style={active ? { color: config.appColor } : {}}
                              >
                                {item.name}
                              </span>
                            )}
                          </Link>
                        )}
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  );

                  if (needsProgramPermission) {
                    return (
                      <PermissionGuard
                        key={item.name}
                        permissionCode={PERMISSION_CODES.VIEW_PROGRAM}
                        fallback={null}
                      >
                        {menuItem}
                      </PermissionGuard>
                    );
                  }

                  if (needsMerchantPermission) {
                    return (
                      <PermissionGuard
                        key={item.name}
                        permissionCode={PERMISSION_CODES.VIEW_MERCHANT}
                        fallback={null}
                      >
                        {menuItem}
                      </PermissionGuard>
                    );
                  }

                  if (needsTransactionPermission) {
                    return (
                      <PermissionGuard
                        key={item.name}
                        permissionCode={PERMISSION_CODES.VIEW_TRANSACTION}
                        fallback={null}
                      >
                        {menuItem}
                      </PermissionGuard>
                    );
                  }

                  if (needsInvoicePermission) {
                    return (
                      <PermissionGuard
                        key={item.name}
                        permissionCode={PERMISSION_CODES.VIEW_INVOICE}
                        fallback={null}
                      >
                        {menuItem}
                      </PermissionGuard>
                    );
                  }

                  return menuItem;
                })}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        ))}
      </div>
      <SidebarFooter className="mt-auto border-t border-gray-200 p-3">
        <div className="flex flex-col gap-2">
          <AppSwitcher />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div
                className={cn(
                  "flex items-center gap-3 p-2 rounded-md cursor-pointer hover:bg-gray-50 transition-colors",
                  expanded ? "justify-start" : "justify-center",
                )}
              >
                {expanded ? (
                  <>
                    <Avatar className="h-8 w-8 border border-gray-200">
                      <AvatarImage src={userInfo?.userProfileImageUri} />
                      <AvatarFallback className="bg-gray-200">
                        {userInfo?.name ? getUserInitials(userInfo.name) : "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 truncate">
                      <p className="text-sm font-medium text-gray-900">
                        {userInfo?.name ? formatName(userInfo.name) : "User"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {userInfo?.username || "user@example.com"}
                      </p>
                    </div>
                  </>
                ) : (
                  <Avatar className="h-8 w-8 mx-auto border border-gray-200">
                    <AvatarImage src={userInfo?.userProfileImageUri} />
                    <AvatarFallback className="bg-gray-200">
                      {userInfo?.name ? getUserInitials(userInfo.name) : "U"}
                    </AvatarFallback>
                  </Avatar>
                )}
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              className="w-max min-w-56 max-w-[min(24rem,calc(100vw-2rem))]"
            >
              <div className="flex items-center gap-2 p-2">
                <Avatar className="h-8 w-8 shrink-0">
                  <AvatarImage src={userInfo?.userProfileImageUri} />
                  <AvatarFallback>
                    {userInfo?.name ? getUserInitials(userInfo.name) : "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex flex-col space-y-0.5 min-w-0">
                  <p className="text-sm font-medium whitespace-nowrap">
                    {userInfo?.name ? formatName(userInfo.name) : "User"}
                  </p>
                  <p className="text-xs text-muted-foreground whitespace-nowrap">
                    {userInfo?.username || "user@example.com"}
                  </p>
                  {userInfo?.roleCode && (
                    <p className="text-xs text-muted-foreground mt-1">
                      {formatRole(userInfo.roleCode)}
                    </p>
                  )}
                </div>
              </div>
              <DropdownMenuSeparator />
              {userInfo?.roleCode !== "PROGRAM_ADMIN" && (
                <DropdownMenuItem asChild>
                  <Link
                    to="/settings"
                    onClick={handleMobileNavigation}
                    className="cursor-pointer"
                  >
                    <Settings className="mr-2 h-4 w-4" />
                    <span>Settings</span>
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                onClick={handleLogout}
                className="cursor-pointer text-red-600 focus:text-red-600"
              >
                <LogOut className="mr-2 h-4 w-4" />
                <span>Log out</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </SidebarFooter>
    </Sidebar>
  );
}
